import React, { useId, useRef, useState } from "react";
import PropTypes from "prop-types";
import { AnimatePresence } from "framer-motion";

import { cn } from "../../utils/classNames";
import { getPrimaryRating, getShowCardRatingTooltip, hasImdbRating } from "../../utils/showRatings";
import RatingBadge from "./RatingBadge";
import Tooltip from "./Tooltip";

const RatingTooltipBadge = ({
  show,
  isArchived = false,
  plain = false,
  badgeClassName,
  tooltipClassName,
  position = "top",
}) => {
  const generatedId = useId();
  const tooltipId = `rating-tooltip-${generatedId.replace(/:/g, "")}`;
  const anchorRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const rating = getPrimaryRating(show);

  if (rating === null) {
    return null;
  }

  const tooltip = getShowCardRatingTooltip(show, { isArchived });
  const badgeLabel = rating.toFixed(1);
  // Plain reads "IMDb 8.4" inside a line of text; a dotted underline marks the explanation.
  const badge = plain ? (
    <span className={tooltip ? "underline decoration-dotted underline-offset-4" : undefined}>
      {hasImdbRating(show) ? "IMDb" : "Viewer rating"} {badgeLabel}
    </span>
  ) : (
    <RatingBadge variant="rating" className={badgeClassName}>
      {badgeLabel}
    </RatingBadge>
  );

  const openTooltip = () => {
    if (tooltip) setIsOpen(true);
  };

  const closeTooltip = () => setIsOpen(false);

  if (!tooltip) {
    return badge;
  }

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={cn(
          "inline-flex appearance-none border-0 bg-transparent p-0 text-inherit",
          "cursor-help focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md"
        )}
        aria-label={`${hasImdbRating(show) ? "IMDb rating" : "Viewer rating"} ${badgeLabel}. Show rating explanation`}
        aria-describedby={isOpen ? tooltipId : undefined}
        aria-expanded={isOpen}
        onMouseEnter={openTooltip}
        onMouseLeave={closeTooltip}
        onFocus={openTooltip}
        onBlur={closeTooltip}
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            closeTooltip();
            anchorRef.current?.focus();
          }
        }}
      >
        {badge}
      </button>

      <AnimatePresence>
        {isOpen && tooltip && (
          <Tooltip
            id={tooltipId}
            position={position}
            className={tooltipClassName}
            anchorRef={anchorRef}
            onClose={closeTooltip}
          >
            {tooltip}
          </Tooltip>
        )}
      </AnimatePresence>
    </>
  );
};

RatingTooltipBadge.propTypes = {
  show: PropTypes.object.isRequired,
  isArchived: PropTypes.bool,
  plain: PropTypes.bool,
  badgeClassName: PropTypes.string,
  tooltipClassName: PropTypes.string,
  position: PropTypes.oneOf(["top", "bottom", "left", "right"]),
};

export default RatingTooltipBadge;
